/**
 * The shift window the «ХОД СМЕНЫ» axis is drawn across (TASK-5080).
 *
 * **There is no tenant-level shift END.** `TenantPlanningSettings` carries
 * `shift_start_time` only (verified: no `shift_end_time` anywhere in the API
 * types), and the per-vehicle `shift_start`/`shift_end` pair describes ONE
 * vehicle, not the day. So the window's close is derived from the vehicles
 * actually out today when they say anything, and the section is omitted when
 * nothing does — the mockup's «08:30—17:30» is illustrative, and hardcoding it
 * would print a shift the tenant never configured.
 */

/** A resolved shift window, both ends as «HH:MM» plus minutes since midnight. */
export interface ShiftWindow {
  startLabel: string;
  endLabel: string;
  startMinutes: number;
  endMinutes: number;
}

/**
 * Parses «HH:MM» or «HH:MM:SS» into minutes since midnight, or null.
 *
 * Null for anything unparsable rather than 0 — midnight is a legitimate shift
 * start, so a silent fallback to 0 would be indistinguishable from one.
 */
export function parseClockMinutes(
  value: string | null | undefined,
): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Formats minutes-since-midnight back to «HH:MM». */
export function formatClockMinutes(totalMinutes: number): string {
  const wrapped = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Builds the window from the tenant's configured start and whatever shift ends
 * today's vehicles declare.
 *
 * Returns null when either end is unknown — the timeline is then not rendered
 * at all, rather than drawn across an invented day.
 *
 * `end` is the LATEST vehicle shift end, because the shift is over only when
 * the last van is back; taking an average or the earliest would mark the day
 * finished while routes were still running.
 */
export function buildShiftWindow(
  shiftStartTime: string | null | undefined,
  vehicleShiftEnds: ReadonlyArray<string | null | undefined>,
): ShiftWindow | null {
  const startMinutes = parseClockMinutes(shiftStartTime);
  if (startMinutes == null) return null;

  const ends = vehicleShiftEnds
    .map(parseClockMinutes)
    .filter((m): m is number => m != null && m > startMinutes);
  if (ends.length === 0) return null;

  const endMinutes = Math.max(...ends);
  return {
    startLabel: formatClockMinutes(startMinutes),
    endLabel: formatClockMinutes(endMinutes),
    startMinutes,
    endMinutes,
  };
}

/**
 * Evenly spaced hour ticks across the window, first and last inclusive.
 *
 * `count` is 6 at the design width; the component hides the inner ones below
 * `sm`, so the axis degrades to three labels on a phone without this function
 * needing to know the viewport.
 */
export function buildHourLabels(window: ShiftWindow, count = 6): string[] {
  const steps = Math.max(2, count);
  const span = window.endMinutes - window.startMinutes;
  return Array.from({ length: steps }, (_, i) =>
    formatClockMinutes(window.startMinutes + (span * i) / (steps - 1)),
  );
}

/**
 * Index of the tick nearest "now", or null when now is outside the window.
 *
 * Outside the window there is no «сейчас» to mark, and emphasising the nearest
 * edge instead would label 07:00 as the current moment of a shift that has not
 * opened.
 */
export function nowTickIndex(
  window: ShiftWindow,
  nowMinutes: number,
  tickCount: number,
): number | null {
  if (nowMinutes < window.startMinutes || nowMinutes > window.endMinutes) {
    return null;
  }
  const span = window.endMinutes - window.startMinutes;
  if (span <= 0 || tickCount < 2) return null;
  const ratio = (nowMinutes - window.startMinutes) / span;
  return Math.round(ratio * (tickCount - 1));
}

/** Minutes since midnight of the current moment, in the tenant's timezone. */
export function tenantNowMinutes(tz: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return hour * 60 + minute;
}
